import React, { useEffect, useState } from "react";
import { deleteDoc, doc, onSnapshot } from "firebase/firestore";
import { useLocation } from "react-router-dom";
import { db } from "../../firebaseConfig";
import { isUserAdmin } from "../../helpers/firestoreService";
import { useAuthUser } from "../../hooks/AuthProvider";
import { MaintenancePage } from "../../pages/System/SystemStatusPage";
import FullScreenSpinner from "./FullScreenSpinner";

const PUBLIC_INFORMATION_PATHS = new Set([
  "/",
  "/contact",
  "/pricing",
  "/terms-of-service",
  "/privacy-policy",
  "/tenant-screening-policy",
  "/fair-housing-policy",
  "/community-guidelines",
  "/payments-refund-policy",
]);

const MaintenanceGate = ({ children }) => {
  const location = useLocation();
  const user = useAuthUser();
  const normalizedPath = location.pathname.length > 1
    ? location.pathname.replace(/\/+$/, "")
    : "/";
  const isPublicInformationPage = PUBLIC_INFORMATION_PATHS.has(normalizedPath)
    || /^\/pm\/[^/]+$/.test(normalizedPath);
  const [status, setStatus] = useState({ loading: true, enabled: false, message: "" });
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);
  const [ending, setEnding] = useState(false);

  useEffect(() => onSnapshot(
    doc(db, "system", "maintenance"),
    (snapshot) => {
      const data = snapshot.exists() ? snapshot.data() : {};
      setStatus({ loading: false, enabled: snapshot.exists(), message: data.message || "" });
    },
    (error) => {
      console.error("Unable to read maintenance status:", error);
      setStatus({ loading: false, enabled: false, message: "" });
    }
  ), []);

  useEffect(() => {
    if (!status.enabled || isPublicInformationPage || !user) {
      setIsAdmin(false);
      setAdminLoading(false);
      return;
    }

    let active = true;
    setAdminLoading(true);
    isUserAdmin(user.uid)
      .then((result) => active && setIsAdmin(result))
      .catch(() => active && setIsAdmin(false))
      .finally(() => active && setAdminLoading(false));

    return () => { active = false; };
  }, [status.enabled, isPublicInformationPage, user?.uid]);

  const handleEndMaintenance = async () => {
    setEnding(true);
    try {
      await deleteDoc(doc(db, "system", "maintenance"));
    } catch (error) {
      console.error("Unable to end maintenance mode:", error);
      setEnding(false);
    }
  };

  if (isPublicInformationPage) return children;
  if (status.loading || (status.enabled && adminLoading)) return <FullScreenSpinner />;
  if (status.enabled) {
    return (
      <MaintenancePage
        message={status.message}
        onEndMaintenance={isAdmin ? handleEndMaintenance : undefined}
        busy={ending}
      />
    );
  }
  return children;
};

export default MaintenanceGate;